import {
	PART_CONFIGURATION_NOT_SAVED,
	PartConfiguratorTreeResult,
} from "src/components/views/erp/configurator/configuratorForm/configuratorTree/PartConfiguratorTreeStatefulView.tsx";
import { ConfigurationCreationApi } from "src/api/generated/erp/configurator/api/configurationCreationApi";
import { CloseDialogFunc, useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { PartConfigurationDetailsView } from "src/components/views/erp/configurator/inspecting/PartConfigurationDetailsView.tsx";
import i18n from "i18next";
import { askObjectConfigurationHistoryEntryInput } from "src/components/views/erp/configurator/objectConfigurationHistory/ObjectConfigurationHistoryEntryInputForm.utils.tsx";
import { CustomerOrderLinePartConfiguratorType } from "src/api/generated/erp/configurator/configuratorType/impl/customerOrderLinePartConfiguratorType.ts";
import { PartConfiguratorTopLevelView } from "src/components/views/erp/configurator/configuratorForm/PartConfiguratorTopLevelView.tsx";
import { DataDirtyStateChangeHandler } from "src/utils/dataDirtyStateChangeHandler.ts";
import { useTenantCustomizations } from "src/tenantCustomizations/TenantCustomizationsContext.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";
import { getPartConfiguratorAgentContextData } from "src/components/views/erp/configurator/configuratorForm/utils/partConfiguratorTypeUtils.ts";

export interface CustomerOrderLineConfiguratorViewProps {
	closeDialog: CloseDialogFunc;
	refreshSourceView?: () => void;
	disabled?: boolean;
	configuratorType: CustomerOrderLinePartConfiguratorType;
	customerOrderId: number;
	currentPartConfigurationId: number | null | undefined;
	onDataDirtyStateChanged?: DataDirtyStateChangeHandler;
}

export const CustomerOrderLineConfiguratorView = ({
	closeDialog,
	refreshSourceView,
	disabled,
	configuratorType,
	customerOrderId,
	currentPartConfigurationId,
	onDataDirtyStateChanged,
}: CustomerOrderLineConfiguratorViewProps) => {
	const { openDialog } = useGenericDialog();
	const tenantCustomizations = useTenantCustomizations();
	const afterCustomerOrderLineConfigurationCreatedView =
		tenantCustomizations.erp?.sales?.afterCustomerOrderLineConfigurationCreatedView;

	return (
		<PartConfiguratorTopLevelView
			configuratorType={configuratorType}
			readonly={disabled}
			onDataDirtyStateChanged={onDataDirtyStateChanged}
			onCompleted={async (result) => {
				if (result.type === "success") {
					return await onSubmit(result.value);
				} else {
					await closeDialog?.({ confirmIfEdited: true });
				}
			}}
		/>
	);

	async function onSubmit({
		propertyValues,
		propertyInputValues,
		configurationSessionId,
		configuratorInput,
		checkpointState,
	}: PartConfiguratorTreeResult) {
		const historyEntryInput = await askObjectConfigurationHistoryEntryInput({
			openDialog,
			objectType: "CUSTOMER_ORDER_LINE",
			objectId: configuratorType.customerOrderLineId,
			configurationSessionId,
			configuratorType: configuratorType,
			configuratorInput,
			checkpointState,
		});
		if (historyEntryInput === undefined) return PART_CONFIGURATION_NOT_SAVED;

		const partConfigurationId = await ConfigurationCreationApi.createPartConfigurationForCustomerOrderLine({
			customerOrderLineId: configuratorType.customerOrderLineId,
			propertyValues: propertyValues,
			propertyInputValues: propertyInputValues,
			configurationSessionId: configurationSessionId,
			historyEntryInput: historyEntryInput,
			testContextEnabled: configuratorType.testContextEnabled,
		});

		await closeDialog();
		refreshSourceView?.();

		if (afterCustomerOrderLineConfigurationCreatedView != null) {
			openDialog(({ closeDialog: closeAfterDialog }) => ({
				title: afterCustomerOrderLineConfigurationCreatedView.title,
				size: "md",
				content: (
					<afterCustomerOrderLineConfigurationCreatedView.component
						customerOrderId={customerOrderId}
						customerOrderLineId={configuratorType.customerOrderLineId}
						newPartConfigurationId={partConfigurationId}
						oldPartConfigurationId={currentPartConfigurationId}
						historyEntryInput={historyEntryInput}
						onCompleted={async () => {
							await closeAfterDialog();
						}}
					/>
				),
				agentContext: {
					key: defineAgentUiContextKey("erp.configurator.afterCustomerOrderLineConfigurationCreated.dialog"),
					title: afterCustomerOrderLineConfigurationCreatedView.title,
					data: {
						...getPartConfiguratorAgentContextData(configuratorType),
						customerOrderId: customerOrderId,
						newPartConfigurationId: partConfigurationId,
						oldPartConfigurationId: currentPartConfigurationId ?? null,
					},
				},
			}));
		} else {
			openDialog(() => ({
				title: i18n.t("configuration"),
				content: (
					<PartConfigurationDetailsView
						partConfigurationId={partConfigurationId}
						partConfiguratorType={configuratorType}
					/>
				),
				agentContext: {
					key: defineAgentUiContextKey("erp.configurator.customerOrderLinePartConfiguration.dialog"),
					title: i18n.t("configuration"),
					data: {
						...getPartConfiguratorAgentContextData(configuratorType),
						partConfigurationId: partConfigurationId,
					},
				},
			}));
		}
	}
};
